import React from "react";
import { FaEye } from "react-icons/fa";
import { FaEyeSlash } from "react-icons/fa6";
import Sidebar from "../menu/Sidebar";
import SidebarAdmin from "../menu/SidebarAdmin";
import Topbar from "../menu/Topbar";
import RealgemsSidebar from "../realgems/RealgemsSidebar";
import Api from "../Services/Api";
import { useAuth } from "../auth/AuthContext";
import {
  canAccessAdminArea,
  canAccessRealgemsArea,
  normalizeAccessType,
} from "../../utils/accessControl";
import "./ChangePassword.css";

const PASSWORD_RULES = [
  { id: "length", label: "Pelo menos 8 caracteres", test: (value) => String(value || "").length >= 8 },
  { id: "upper", label: "Ao menos 1 letra maiúscula", test: (value) => /[A-Z]/.test(String(value || "")) },
  { id: "lower", label: "Ao menos 1 letra minúscula", test: (value) => /[a-z]/.test(String(value || "")) },
  { id: "number", label: "Ao menos 1 número", test: (value) => /\d/.test(String(value || "")) },
  { id: "special", label: "Ao menos 1 caractere especial", test: (value) => /[^A-Za-z0-9]/.test(String(value || "")) },
];

function getPasswordStrength(value) {
  const passedRules = PASSWORD_RULES.filter((rule) => rule.test(value)).length;
  if (!value) return { label: "Não informada", tone: "neutral", progress: 0 };
  if (passedRules <= 2) return { label: "Fraca", tone: "weak", progress: 33 };
  if (passedRules <= 4) return { label: "Média", tone: "medium", progress: 66 };
  return { label: "Forte", tone: "strong", progress: 100 };
}

const ChangePassword = () => {
  const { user, updateUser } = useAuth();
  const [menuClose, setMenuClose] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const accessType = normalizeAccessType(user?.access_type);
  const isAdmin = canAccessAdminArea(accessType);
  const isRealgems = canAccessRealgemsArea(accessType);
  const toggleMenu = () => setMenuClose((current) => !current);
  const strength = getPasswordStrength(newPassword);
  const confirmMatches = confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordIsStrong = PASSWORD_RULES.every((rule) => rule.test(newPassword));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Preencha todos os campos para alterar a senha.");
      return;
    }

    if (!passwordIsStrong) {
      setError("A nova senha precisa atender todos os requisitos de segurança.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("A confirmação da nova senha não confere.");
      return;
    }

    try {
      setSaving(true);
      const response = await Api.post("/auth/change-password", {
        currentPassword,
        newPassword,
        confirmPassword,
      });

      if (response?.data?.user) {
        updateUser(response.data.user);
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess(response?.data?.message || "Senha alterada com sucesso.");
    } catch (err) {
      setError(err?.response?.data?.message || "Não foi possível alterar a senha.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`app-container${isRealgems ? " realgems-dashboard-page" : ""}`}>
      {isAdmin ? (
        <SidebarAdmin close={menuClose} />
      ) : isRealgems ? (
        <RealgemsSidebar close={menuClose} />
      ) : (
        <Sidebar close={menuClose} />
      )}

      <main className={`main-content${isRealgems ? " realgems-main-content" : ""}`}>
        <Topbar page="Minha conta / Alterar senha" onToggleMenu={toggleMenu} />

        <section className="change-password-page">
          <article className="change-password-card">
            <div className="change-password-header">
              <div>
                <span className="change-password-eyebrow">Segurança da conta</span>
                <h1>Alterar senha</h1>
                <p>
                  Informe sua senha atual e defina uma nova senha forte para todos os próximos acessos.
                </p>
              </div>
            </div>

            <form className="change-password-form" onSubmit={handleSubmit}>
              <div className="change-password-grid">
                <div className="change-password-main-column">
                  <label className="change-password-field" htmlFor="current-password">
                    <span>Senha atual</span>
                    <div className="change-password-input-wrap">
                      <input
                        id="current-password"
                        type={showCurrentPassword ? "text" : "password"}
                        autoComplete="current-password"
                        value={currentPassword}
                        onChange={(event) => setCurrentPassword(event.target.value)}
                        placeholder="Digite sua senha atual"
                      />
                      <button
                        type="button"
                        className="change-password-eye"
                        onClick={() => setShowCurrentPassword((value) => !value)}
                        aria-label={showCurrentPassword ? "Ocultar senha atual" : "Mostrar senha atual"}
                        title={showCurrentPassword ? "Ocultar senha atual" : "Mostrar senha atual"}
                      >
                        {showCurrentPassword ? (
                          <FaEye className="change-password-eye-icon" size={18} />
                        ) : (
                          <FaEyeSlash className="change-password-eye-icon" size={18} />
                        )}
                      </button>
                    </div>
                  </label>

                  <label className="change-password-field" htmlFor="confirm-password">
                    <span>Confirmar nova senha</span>
                    <div className="change-password-input-wrap">
                      <input
                        id="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                        placeholder="Repita a nova senha"
                      />
                      <button
                        type="button"
                        className="change-password-eye"
                        onClick={() => setShowConfirmPassword((value) => !value)}
                        aria-label={
                          showConfirmPassword
                            ? "Ocultar confirmação da nova senha"
                            : "Mostrar confirmação da nova senha"
                        }
                        title={
                          showConfirmPassword
                            ? "Ocultar confirmação da nova senha"
                            : "Mostrar confirmação da nova senha"
                        }
                      >
                        {showConfirmPassword ? (
                          <FaEye className="change-password-eye-icon" size={18} />
                        ) : (
                          <FaEyeSlash className="change-password-eye-icon" size={18} />
                        )}
                      </button>
                    </div>
                  </label>

                  <div className="change-password-strength">
                    <div className="change-password-strength-top">
                      <strong>Força da senha</strong>
                      <span className={`tone-${strength.tone}`}>{strength.label}</span>
                    </div>
                    <div className="change-password-strength-bar">
                      <div
                        className={`change-password-strength-fill tone-${strength.tone}`}
                        style={{ width: `${strength.progress}%` }}
                      />
                    </div>
                  </div>

                  {confirmPassword ? (
                    <div className={`change-password-confirm${confirmMatches ? " passed" : ""}`}>
                      {confirmMatches
                        ? "Confirmação da senha correta."
                        : "A confirmação da senha não confere."}
                    </div>
                  ) : null}
                </div>

                <div className="change-password-side-column">
                  <label className="change-password-field" htmlFor="new-password">
                    <span>Nova senha</span>
                    <div className="change-password-input-wrap">
                      <input
                        id="new-password"
                        type={showNewPassword ? "text" : "password"}
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        placeholder="Crie uma nova senha forte"
                      />
                      <button
                        type="button"
                        className="change-password-eye"
                        onClick={() => setShowNewPassword((value) => !value)}
                        aria-label={showNewPassword ? "Ocultar nova senha" : "Mostrar nova senha"}
                        title={showNewPassword ? "Ocultar nova senha" : "Mostrar nova senha"}
                      >
                        {showNewPassword ? (
                          <FaEye className="change-password-eye-icon" size={18} />
                        ) : (
                          <FaEyeSlash className="change-password-eye-icon" size={18} />
                        )}
                      </button>
                    </div>
                  </label>

                  <div className="change-password-rules-panel">
                    <div className="change-password-rules-header">
                      <strong>Requisitos da senha</strong>
                      <span>Preencha todos para continuar</span>
                    </div>

                    <div className="change-password-rules">
                      {PASSWORD_RULES.map((rule) => {
                        const passed = rule.test(newPassword);
                        return (
                          <div
                            key={rule.id}
                            className={`change-password-rule${passed ? " passed" : ""}`}
                          >
                            {rule.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {error ? <div className="change-password-message error">{error}</div> : null}
              {success ? <div className="change-password-message success">{success}</div> : null}

              <div className="change-password-actions">
                <button
                  type="submit"
                  disabled={saving || !passwordIsStrong || !confirmMatches}
                >
                  {saving ? "Salvando..." : "Alterar senha"}
                </button>
              </div>
            </form>
          </article>
        </section>
      </main>
    </div>
  );
};

export default ChangePassword;
